import {createFileRoute, Navigate, useNavigate} from "@tanstack/react-router";
import {
    Box,
    Button,
    Card,
    DialogActions,
    DialogContent,
    DialogTitle,
    Modal,
    ModalDialog,
    Sheet,
    Stack,
    Typography,
    useTheme
} from "@mui/joy";
import {useQuery} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {HCBox} from "../../components/HCBox.tsx";
import {useSelf} from "../../agent/useSelf.ts";
import {
    ensureScheduledTransferById,
    Job,
    ScheduledTransferPatch,
    useBundleById,
    useJobById,
    useScheduledTransferById,
    watchBundleByIdTqo,
} from "../../rpc/webrpc-client.gen.ts";
import {faClipboardCheck, faTruckClock} from "@fortawesome/pro-regular-svg-icons";
import {iconForEntityType} from "../../fa.tsx";
import {useMountedTruck} from "../../driver/mountedTruck.ts";
import {ReactNode, useCallback, useEffect, useState} from "react";
import {DisclaimScheduledTransferDialog} from "../../driver/claimScheduledTransfer.tsx";
import {faFaceEyesXmarks, faHandHeart, faQuestion, faTimes} from "@fortawesome/pro-solid-svg-icons";
import {VenueCapsule} from "../../venue/capsule.tsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ActionButton} from "../../components/ActionButton.tsx";
import {useTriplegStarter} from "../../transit/tripleg.tsx";
import {ArriveButton} from "../../transit/ArriveButton.tsx";
import {useTruckStatus} from "../../truck/truck.tsx";
import {FieldCancelButton} from "../../components/FieldCancelButton.tsx";
import {
    useMothership,
    useMountTruckMutation,
    usePatchScheduledTransferMutation,
    usePatchTriplegMutation
} from "../../tq/tqx.tsx";
import {SuspenseDialog} from "../../components/SuspenseDialog.tsx";
import {BundleTile, BundleTileProps} from "../../bundle/tile.tsx";
import {CompanyCapsule} from "../../company/capsule.tsx";
import {useBundleDestinationCapsule} from "../../driver/DriverBundleTile.tsx";
import {useNaviScratchBlockDispatch} from "../../navi/Navi.tsx";
import {join} from "remeda";
import {PageBody, PageHeader, PageShell} from "../../routing/shell.tsx";
import {TopBar} from "../../components/TopBar.tsx";

interface PickupBundleTileProps extends BundleTileProps {
    job?: Job;
}

function PickupBundleTile({bundleStub, job, ...rest}: PickupBundleTileProps) {
    const mama = useMothership();
    const bundleTqrv = useQuery(watchBundleByIdTqo(mama, {bundleId: bundleStub.id}));
    const bundle = bundleTqrv.data;
    const destinationCapsule = useBundleDestinationCapsule(bundle);

    return <BundleTile bundleStub={bundleStub} {...rest}
                       color="neutral" variant="outlined">
        {job?.sendCompany ? <CompanyCapsule company={job.sendCompany}/> : null}
        {destinationCapsule}
    </BundleTile>;
}

export function ScheduledTransferView() {
    const {t} = useTranslation();
    const {id} = Route.useParams();
    const scheduledTransferTqrv
        = useScheduledTransferById({scheduledTransferId: id});
    const sxfer = scheduledTransferTqrv.data;
    const selfTqrv = useSelf();
    const self = selfTqrv.data;
    const isMyTask = selfTqrv.isSuccess && scheduledTransferTqrv.isSuccess
        && (sxfer && sxfer.driver?.id === self?.id);
    const isFreeTask = scheduledTransferTqrv.isSuccess && !!sxfer && !sxfer.driver;
    const mountedTruckTqrv = useMountedTruck();
    const mountedTruck = mountedTruckTqrv.data;
    const truckStatusTqrv = useTruckStatus(
        mountedTruck ? {truckId: mountedTruck.id} : undefined);
    const mountedTruckStatus = truckStatusTqrv.data;
    const mountedTruckLastTripleg = mountedTruckStatus?.lastTripleg;
    const canTruckDepart = mountedTruckStatus?.canDepart ?? false;
    const isEnRoute = !!mountedTruckStatus?.lastTripleg
        && !mountedTruckStatus.lastTripleg.arrived;
    const canRedirect = isEnRoute;
    const currentDestination = mountedTruckLastTripleg?.destination;
    const doesTruckMatch = scheduledTransferTqrv.isSuccess && sxfer?.truck?.id === mountedTruck?.id;
    const {mutateAsync: mountTruck} = useMountTruckMutation();
    const {mutateAsync: patchScheduledTransfer} = usePatchScheduledTransferMutation();
    const stakeClaim = useCallback(() => {
        if (sxfer && self) {
            return patchScheduledTransfer({
                transferId: sxfer.id,
                patch: {driverId: self.id}
            });
        }
    }, [patchScheduledTransfer, self, sxfer]);
    const [isDisclaimDialogOpen, setIsDisclaimDialogOpen]
        = useState<boolean>(false);
    const startTripleg = useTriplegStarter();
    const navigate = useNavigate();
    const {mutateAsync: patchTripleg} = usePatchTriplegMutation();
    const redirectTripleg = useCallback(() => {
        if (scheduledTransferTqrv.isSuccess && !!sxfer && !!mountedTruckLastTripleg?.id) {
            return patchTripleg({
                triplegId: mountedTruckLastTripleg.id,
                patch: {destinationId: sxfer.venue.id},
            });
        } else {
            return Promise.reject(new Error("no tripleg to redirect"));
        }
    }, [mountedTruckLastTripleg?.id, patchTripleg, scheduledTransferTqrv.isSuccess, sxfer]);

    const onStartDisclaim = useCallback(() => {
        setIsDisclaimDialogOpen(true);
    }, []);

    const onStartClaim = useCallback(() => {
        // TODO: confirmation dialog?
        return stakeClaim();
    }, [stakeClaim]);

    const onStartTruckMount = useCallback(() => {
        if (!mountedTruck && sxfer?.truck) {
            return mountTruck({truckId: sxfer.truck.id})
        } else {
            return Promise.resolve();
        }
    }, [mountTruck, mountedTruck, sxfer?.truck]);

    const onStartTruckPatch = useCallback(() => {
        if (mountedTruck && sxfer?.id) {
            const patch: ScheduledTransferPatch = {
                truckId: mountedTruck.id,
            }
            return patchScheduledTransfer({transferId: sxfer.id, patch});
        } else {
            console.log(`truck patch failed mountedTruck ${JSON.stringify(mountedTruck)} sxfer ${JSON.stringify(sxfer)}`);
            return Promise.resolve();
        }
    }, [mountedTruck, patchScheduledTransfer, sxfer]);

    const bundleTqrv = useBundleById(
        sxfer ? {bundleId: sxfer.bundle.id} : undefined);
    const bundle = bundleTqrv.data;

    const jobTqrv = useJobById(bundle ? {jobId: bundle.job.id} : undefined);
    const job = jobTqrv.data;
    const jobExtras = job?.extras;
    const driverNotes = jobExtras?.shinwa?.driverNotes
        ? join(jobExtras.shinwa.driverNotes, "\n")
        : jobExtras?.sanyu?.driverNotes;
    const photoRequired = jobExtras?.shinwa?.photoRequired;
    const orderContactName = jobExtras?.shinwa?.orderContactName ?? jobExtras?.sanyu?.orderContact;
    const specialNotes = jobExtras?.shinwa?.specialNotes;
    const orderNotes = jobExtras?.shinwa?.orderNotes
        ? join(jobExtras.shinwa.orderNotes, "\n") : undefined

    const theme = useTheme();
    const [isRedirectConfirmOpen, setIsRedirectConfirmOpen]
        = useState<boolean>(false);

    const dispatchScratchNaviItem = useNaviScratchBlockDispatch();
    useEffect(() => {
        if (sxfer && bundle) {
            dispatchScratchNaviItem({
                to: Route.to,
                params: {id},
                icon: {icon: faTruckClock},
                children: bundle.displayName,
            });
        }
    }, [bundle, dispatchScratchNaviItem, id, sxfer]);

    const venue = sxfer?.venue;
    const onStartTripleg = useCallback(() => {
        if (venue) {
            return startTripleg(venue.id);
        } else {
            return Promise.reject(new Error("no venue"));
        }
    }, [startTripleg, venue]);

    if (!selfTqrv.isSuccess || !bundleTqrv.isSuccess || !scheduledTransferTqrv.isSuccess
        || !mountedTruckTqrv.isSuccess) {
        return <SuspenseDialog message="sxfer"/>;
    }

    if (!sxfer) {
        return <Navigate to="/driver/schedule"></Navigate>
    }

    if (!bundle) {
        throw new Error(`bundle ${sxfer.bundle.id} is not active`);
    }

    const driver = sxfer.driver;

    const driverProgress = isMyTask ? {
        palette: theme.palette.success,
        icon: faClipboardCheck
    } : {
        palette: theme.palette.warning,
        icon: faTimes
    };
    const truck = sxfer.truck;
    const truckProgress = truck
        ? (doesTruckMatch ? {
            palette: theme.palette.success,
            icon: faClipboardCheck
        } : {
            palette: theme.palette.neutral,
            icon: faTimes
        }) : {
            palette: theme.palette.neutral,
            icon: faQuestion,
        };
    const claimButtonVariant = isMyTask || isFreeTask ? "soft" : "solid";
    const claimButtonColor = isMyTask || !isFreeTask ? "danger" : "primary";
    const claimButtonFood = isMyTask ? {
        icon: faFaceEyesXmarks,
        label: t("driver.disclaim"),
        clickHandler: onStartDisclaim,
    } : {
        icon: faHandHeart,
        label: t("driver.claim"),
        clickHandler: onStartClaim,
    };

    let actionZone: ReactNode = null;
    if (truck && isMyTask && doesTruckMatch) {
        if (canTruckDepart) {
            actionZone = <ActionButton sx={{py: 3, px: 8, fontSize: theme.fontSize.xl2}}
                                       perform={() => onStartTripleg()}>
                {t("driver.startTripleg")}
            </ActionButton>;
        } else {
            const isInbound = mountedTruckLastTripleg?.destination.id === sxfer.venue.id;
            actionZone = <Stack direction="column" spacing={3}
                                sx={{alignItems: "center", width: "80%"}}>
                <Sheet color={isInbound ? "primary" : "danger"} variant="soft"
                       sx={{fontSize: "xl", width: "100%", textAlign: "center", py: 1.2}}>
                    {t(isInbound ? "driver.enRoute" : "driver.enRouteElsewhere")}
                </Sheet>
                <Stack direction="row" sx={{alignItems: "center", width: "100%"}}>
                    <Box sx={{flex: 1}}/>
                    <FieldCancelButton bundle={bundle} sx={{fontSize: "md", flex: 2, py: 1.6}}/>
                    <Box sx={{flex: 1}}/>
                    {isInbound
                        ? <ArriveButton truckId={mountedTruck!.id} depth="brief"
                                        color="primary" variant="solid"
                                        veilSx={{flex: 5}}
                                        sx={{fontSize: "xl", py: 2.6}}/>
                        : <Button color="primary" variant="solid" sx={{fontSize: "xl", flex: 5, py: 3}}
                                  disabled={!canRedirect}
                                  onClick={() => {
                                      setIsRedirectConfirmOpen(true);
                                  }}>
                            {canRedirect
                                ? t("driver.redirectToHere")
                                : t("driver.cannotRedirect")}
                        </Button>}
                    <Box sx={{flex: 1}}/>
                </Stack>
                <Modal open={isRedirectConfirmOpen}
                       onClose={() => {
                           setIsRedirectConfirmOpen(false);
                       }}
                       sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                    <ModalDialog variant="outlined">
                        <DialogTitle>
                            <Box sx={{fontSize: "lg"}}>{t("driver.redirectDialogTitle")}</Box>
                        </DialogTitle>
                        <DialogContent>
                            <Stack direction="column">
                                <Box sx={{fontSize: "lg"}}>
                                    {t("driver.redirectDialogLine1",
                                        {fromLabel: currentDestination?.displayName})}
                                    {t("driver.redirectDialogLine2",
                                        {toLabel: sxfer.venue.displayName})}
                                </Box>
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <ActionButton color="primary" variant="solid" sx={{px: 2.4, py: 1.6}}
                                          disabled={!isEnRoute}
                                          perform={() => {
                                              return redirectTripleg()
                                                  .then(() => navigate({
                                                      to: "/truck/$id/drive",
                                                      params: {id: mountedTruck!.id},
                                                  }))
                                          }}>
                                {t("driver.doChange")}
                            </ActionButton>
                            <Button color="neutral" variant="soft" onClick={() => {
                                setIsRedirectConfirmOpen(false);
                            }}>
                                {t("cancel")}
                            </Button>
                        </DialogActions>
                    </ModalDialog>
                </Modal>
            </Stack>
        }
    }

    return <PageShell>
        <PageHeader>
            <Stack direction="column">
                <TopBar/>

                <Stack direction="row" sx={{justifyContent: "right", width: "100%"}}>
                    <ActionButton variant={claimButtonVariant} color={claimButtonColor}
                                  perform={() => {
                                      claimButtonFood.clickHandler();
                                      return Promise.resolve();
                                  }}
                                  startDecorator={<FontAwesomeIcon icon={claimButtonFood.icon} size="2x"/>}>
                        {claimButtonFood.label}
                    </ActionButton>
                </Stack>
            </Stack>
        </PageHeader>
        <PageBody>
            <Card sx={{minWidth: "85%"}}>
                <Stack direction="column" spacing={2}>
                    <Sheet color="primary" variant="plain">
                        <Stack direction="row" sx={{
                            alignItems: "center", justifyContent: "center", py: 1.2
                        }}>
                            <PickupBundleTile bundleStub={bundle} sx={{width: "auto"}}
                                              photoRequired={photoRequired}
                                              job={job}/>
                        </Stack>
                    </Sheet>
                    {driverNotes
                        ? <Stack direction="row">
                            <Box>{t("driver.driverNotes")}</Box>
                            <Box>{driverNotes}</Box>
                        </Stack>
                        : null}
                    {specialNotes
                        ? <Stack direction="row">
                            <Box>{t("driver.specialNotes")}</Box>
                            <Box>{specialNotes}</Box>
                        </Stack>
                        : null}
                    {orderContactName
                        ? <Stack direction="row">
                            <Box>{t("driver.orderContactLabel")}</Box>
                            <Box>{orderContactName}</Box>
                        </Stack>
                        : null}
                    {orderNotes
                        ? <Stack direction="row">
                            <Box>{t("driver.orderNotesLabel")}</Box>
                            <Box>{orderNotes}</Box>
                        </Stack>
                        : null}
                    <Stack direction="row" sx={{alignItems: "stretch"}}>
                        <Stack direction="row" sx={{
                            justifyContent: "space-between", alignItems: "center"
                        }}>
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: driverProgress.palette.softBg,
                                py: 1.2
                            }}>
                                <FontAwesomeIcon {...iconForEntityType("driver")} size="2x"
                                                 color={driverProgress.palette.softColor}/>
                                <Typography fontSize="xl" sx={{
                                    px: 1.6,
                                    color: driverProgress.palette.softColor,
                                    backgroundColor: driverProgress.palette.softBg
                                }}>
                                    {driver?.displayName ?? t("unset")}</Typography>
                            </Box>
                        </Stack>
                        <Stack direction="column" sx={{
                            flex: 1, display: "flex", alignItems: "center",
                            bgcolor: driverProgress.palette.softBg,
                        }}>
                            <Box sx={{
                                height: "0px", width: "95%", margin: "auto",
                                borderWidth: "0.8px", borderStyle: "dashed",
                                borderColor: driverProgress.palette.softColor
                            }}/>
                        </Stack>
                        <Box sx={{px: 1.6, py: 1.2, bgcolor: driverProgress.palette.softBg}}>
                            <FontAwesomeIcon icon={driverProgress.icon} size="2x"
                                             color={driverProgress.palette.softColor}/>
                        </Box>
                    </Stack>
                    <Stack direction="row" sx={{alignItems: "stretch"}}>
                        <Stack direction="row" sx={{justifyContent: "space-between", alignItems: "center"}}>
                            <Box sx={{
                                display: "flex", alignItems: "center",
                                py: 1.2, bgcolor: truckProgress.palette.softBg,
                            }}>
                                <FontAwesomeIcon {...iconForEntityType("truck")} size="2x"
                                                 color={truckProgress.palette.softColor}/>
                                <Typography fontSize="xl" sx={{
                                    px: 1.6,
                                    color: truckProgress.palette.softColor,
                                    bgcolor: truckProgress.palette.softBg,
                                }}>
                                    {truck?.displayName ?? t("unset")}</Typography>
                            </Box>
                        </Stack>
                        <Stack direction="column" sx={{
                            flex: 1, display: "flex", alignItems: "center",
                            bgcolor: truckProgress.palette.softBg,
                        }}>
                            <Box sx={{
                                height: "0px", width: "95%", margin: "auto",
                                borderWidth: "0.8px", borderStyle: "dashed",
                                borderColor: truckProgress.palette.softColor,
                            }}/>
                        </Stack>
                        <Stack direction="row" spacing={1.2}
                               sx={{
                                   bgcolor: truckProgress.palette[100], px: 1.6, py: 1.2,
                                   alignItems: "center"
                               }}>
                            <FontAwesomeIcon icon={truckProgress.icon} size="2x"
                                             color={truckProgress.palette.softColor}/>
                            {truck && !mountedTruck
                                ? <ActionButton perform={onStartTruckMount}
                                                sx={{mx: "0.5rem", px: 3, py: 1.2, fontSize: "xl"}}>
                                    {t("driver.mountTruck")}</ActionButton>
                                : null}
                        </Stack>
                        {mountedTruck && !doesTruckMatch
                            ? <ActionButton perform={onStartTruckPatch}>
                                {t("exchangeTruck", {plate: mountedTruck.displayName})}
                            </ActionButton>
                            : null}
                    </Stack>
                    {venue
                        ? <HCBox>
                            <VenueCapsule venue={venue} sx={{fontSize: "xl"}}/>
                        </HCBox>
                        : null}
                </Stack>
            </Card>
            <Stack direction="column" sx={{alignItems: "center", width: "100%"}}>
                {actionZone}
            </Stack>
        </PageBody>
        <DisclaimScheduledTransferDialog transfer={sxfer}
                                         isOpen={isDisclaimDialogOpen}
                                         close={() => {
                                             setIsDisclaimDialogOpen(false);
                                         }}/>
    </PageShell>;
}

export const Route = createFileRoute("/_auth/sxfer/$id")({
    component: ScheduledTransferView,
    beforeLoad: ({context, params}) => {
        return ensureScheduledTransferById({
            tqc: context.queryClient,
            mama: context.mothership,
            args: {scheduledTransferId: params.id}
        }).then((tqrv) => {
            if (!tqrv.scheduledTransfer) {
                throw new Error(`no such scheduled transfer ${params.id}`);
            }
            return {
                naviTriggerIcon: iconForEntityType("bundle"),
                naviTriggerLabel: tqrv.scheduledTransfer.bundle.displayName,
            };
        })
            .catch();
    }
});
